import React from 'react';
import { Button, CircularProgress, useMediaQuery } from '@mui/material';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { useTheme, alpha } from '@mui/material/styles';
import { createLogger } from '../../utils/logger';

interface SaveTripButtonProps {
  label?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  disabled?: boolean;
  variant?: 'button' | 'text';
  onSave: () => Promise<string | null>;
}

export const SaveTripButton: React.FC<SaveTripButtonProps> = ({
  label = 'Save',
  size = 'medium',
  fullWidth,
  disabled = false,
  variant = 'button',
  onSave,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);

  const handleSave = async () => {
    if (saving) return;

    setSaving(true);
    setSaved(false);

    try {
      const result = await onSave();
      if (result) {
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      }
    } catch (error) {
      log.error('Failed to save trip:', error);
    } finally {
      setSaving(false);
    }
  };

  const getIcon = () => {
    if (saving) return <CircularProgress size={16} sx={{ color: 'inherit' }} />;
    if (saved) return <CheckCircleRoundedIcon sx={{ color: theme.palette.success.main }} />;
    return <SaveRoundedIcon />;
  };

  const getLabel = () => {
    if (saving) return 'Saving...';
    if (saved) return 'Saved!';
    return label;
  };

  const button = (
    <Button
      size={size}
      startIcon={getIcon()}
      onClick={handleSave}
      disabled={disabled || saving}
      fullWidth={fullWidth}
      variant={variant === 'text' ? 'text' : 'outlined'}
      sx={{
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: 999,
        px: size === 'small' ? 1.5 : 2.5,
        borderColor: saved ? theme.palette.success.main : undefined,
        color: saved ? theme.palette.success.main : undefined,
        '&:hover': {
          borderColor: saved ? theme.palette.success.dark : undefined,
          backgroundColor: saved ? alpha(theme.palette.success.main, 0.04) : undefined,
        },
      }}
    >
      {isSmallScreen && saved ? 'Saved' : getLabel()}
    </Button>
  );

  return button;
};

const log = createLogger('SaveTripButton');
